import FullscreenDecorator from '../lib/FullscreenDecorator.js';
import ConfirmDialog from '../lib/ConfirmDialog.js';
import getDeviceType from '../lib/getDeviceType.js';
import { toast } from '@finn-no/common-browserbundle';

export default {
    dependencies: [
        '$element',
        'searchmanager',
        'templateservice',
        'accountservice',
        '$controllerator',
        'radio',
        'trackingservice',
        'savedsearchesservice',
        'loginservice',
    ],
    // eslint-disable-next-line max-params
    callable(
        element,
        searchmanager,
        templateservice,
        accountservice,
        controllerator,
        radio,
        trackingservice,
        savedsearchesservice,
        loginservice
    ) {
        const url = new URL(document.baseURI);
        let container;
        let dialog;
        const button = element.querySelector('button');
        button.addEventListener('click', handleClick);
        button.addEventListener('keydown', handleKeydown);

        // A login parameter will be added to the SPiD login redirect. This will suggest that the user logged in and the search can be saved
        // immediately after login and SPiD redirected back to saved search.
        const parameterFromSpidLoginRedirect = 'saveSearchToolbarAutoSave';

        if (url.searchParams.has(parameterFromSpidLoginRedirect)) {
            url.searchParams.delete(parameterFromSpidLoginRedirect);
            window.history.replaceState(null, null, url);
            showSaveSearchDialog();
        }

        const isMobile = getDeviceType() === 'mobile';

        radio('filter-and-search-changed').subscribe(result => {
            button.disabled = !result.isSearchStorable;
            button.classList.remove('inprogress');
        });

        function handleClick(event) {
            button.disabled = true;
            button.classList.add('inprogress');
            sendTrackingEventWith('click', '');

            event.stopPropagation();
            showSaveSearchDialog();
            button.disabled = false;
            button.classList.remove('inprogress');
        }

        function handleKeydown(event) {
            if (
                event.keyCode === 13 /* Enter */ ||
                event.keyCode === 32 /* Space */
            ) {
                event.stopPropagation();
                event.preventDefault();
                event.target.click();
            }
        }

        function showSaveSearchDialog() {
            container = document.createElement('div');
            container.className = 'phm';
            container.addEventListener('click', handleContainerClick);
            element.focus();

            const size = {
                width: '300px',
                height: '350px',
            };
            const SavedSearchDialog = isMobile
                ? FullscreenDecorator(ConfirmDialog)
                : ConfirmDialog;
            dialog = new SavedSearchDialog(
                'Lagre søk',
                container,
                'Lagre',
                'Avbryt',
                { onDismiss: handleDialogDismiss, size }
            );
            toggleVisibility(dialog.positiveButton, false);
            dialog.show();
            renderView();
        }

        function handleContainerClick(evt) {
            const ele = evt.target.closest('[data-action]');

            if (!ele || !evt.currentTarget.contains(ele)) {
                return;
            }

            evt.stopPropagation();
            evt.preventDefault();
            const action = ele.getAttribute('data-action');
            if (action === 'update') {
                sendTrackingEventWith('update', '');

                const params = searchmanager.getSearchParams();
                ele.classList.add('inprogress');

                savedsearchesservice
                    .getSavedSearch(searchmanager.savedSearchId)
                    .then(search =>
                        savedsearchesservice.updateSavedSearch(
                            params.vertical,
                            params.segment,
                            params.args,
                            search.title,
                            search.id,
                            search.notifications
                        )
                    )
                    .then(onSaveSuccess, onSaveFail)
                    .then(() => {
                        ele.classList.remove('inprogress');
                    });
            } else if (action === 'show-save-new-opts') {
                sendTrackingEventWith('new', '');
                renderEditor(null, true);
            }
        }

        function handleDialogDismiss(reason) {
            if (reason === ConfirmDialog.POSITIVE_LABEL) {
                saveSearch();
                return true; // Don't close
            } else if (reason === ConfirmDialog.NEGATIVE_LABEL) {
                sendTrackingEventWith('cancel', '');
            }
        }

        function saveSearch() {
            const title = container.querySelector('[name=title]').value;
            const notify = container.querySelector('[name=notify]').checked;

            if (!title || title.trim() === '') {
                renderEditor('Du må gi listen et navn', true);
            } else {
                // TODO: change this to some other indication probably
                dialog.positiveButton.classList.add('inprogress');
                dialog.positiveButton.classList.remove('primary');
                dialog.positiveButton.disabled = true;
                const params = searchmanager.getSearchParams();
                savedsearchesservice
                    .createSavedSearch(
                        params.vertical,
                        params.segment,
                        params.args,
                        title,
                        notify
                    )
                    .then(onSaveSuccess, onSaveFail)
                    .then(() => {
                        dialog.positiveButton.classList.remove('inprogress');
                    });
            }
        }

        function onSaveSuccess() {
            sendTrackingEventWith('done', 'success');

            radio('save-search-success').broadcast();
            dialog.hide();
            toast({ message: 'Søket er lagret!' });
        }

        function onSaveFail() {
            sendTrackingEventWith('try-again', '');
            renderEditor('En feil oppstod under lagring. Prøv igjen senere.');
        }

        function renderView() {
            if (accountservice.isReady()) {
                renderEditor();
                dialog.positiveButton.focus();
            } else {
                container.innerHTML = templateservice.render(
                    'save_search_box',
                    { loading: true }
                );
                accountservice.whenReady().then(renderView);
            }
        }

        function handleSubmit(event) {
            sendTrackingEventWith('save', '');
            event.preventDefault();
            saveSearch();
        }

        function renderEditor(msg, forceNew) {
            savedsearchesservice
                .getSavedSearch(searchmanager.savedSearchId)
                .catch(err => {
                    if (err.status === 401) {
                        return null;
                    } else {
                        throw err;
                    }
                })
                .then(saved => {
                    doRenderEditor(saved, msg, forceNew);
                });
        }

        function doRenderEditor(saved, msg, forceNew) {
            const saveNew = !saved || forceNew;
            const isLoggedIn = loginservice.isLoggedIn();

            const currentPageWithLoginParam = new URL(document.baseURI);
            currentPageWithLoginParam.searchParams.set(
                parameterFromSpidLoginRedirect,
                ''
            );

            toggleVisibility(dialog.positiveButton, !(saveNew && isLoggedIn));

            const context = {
                search: saved,
                loggedIn: isLoggedIn,
                message: msg,
                title: document.title.replace(/.*– /, ''), // Everything before and including "FINN – "
                currentPage: encodeURIComponent(currentPageWithLoginParam),
                loginMessage: 'Logg inn for å lagre søket',
                saveNew: !saved || forceNew,
                searchParamsChanged: searchmanager.searchParamsChanged,
            };
            if (!isLoggedIn) {
                sendTrackingEventWith('need-login', '');
            }
            container.innerHTML = templateservice.render(
                'save_search_box',
                context
            );

            const formEle = container.querySelector("form[name='save-search']");
            if (formEle) {
                formEle.addEventListener('submit', handleSubmit);
            }

            controllerator.run();
        }

        function toggleVisibility(ele, visible) {
            ele.classList.toggle('hidden', visible);
            ele.setAttribute('aria-hidden', visible);
        }

        function sendTrackingEventWith(eventDetail, eventFlag) {
            trackingservice.trackEventObject({
                analyticsName: `search.save-search.${eventDetail}`,
                eventFlags: eventFlag,
                stringValue: 'searchhresult',
            });
        }
    },
};



// WEBPACK FOOTER //
// ./controllers/saveSearch.js